import { RiErrorWarningLine, RiLinkUnlinkM } from "@remixicon/react";
import { Alert } from "@screener/uikit/components/alert";
import { Button } from "@screener/uikit/components/button";
import { IconTile } from "@screener/uikit/components/icon-tile";
import { useCallback, useEffect, useRef, useState } from "react";
import { type ConfirmEmailResult, confirmEmail } from "@/lib/auth-api";
import { AuthHeading, AuthLayout, AuthStatus } from "./auth-layout";

/** Что показывает страница подтверждения, пока не пришёл успех. */
export type ConfirmPageState = "confirming" | "link-invalid" | "unavailable";

/**
 * Исход запроса в состояние страницы; `null` — успех, страница уступает место
 * приложению. `switch` с `satisfies never`: новый исход не останется без экрана.
 */
export function confirmPageState(result: ConfirmEmailResult): ConfirmPageState | null {
  switch (result) {
    case "confirmed":
      return null;
    case "link-invalid":
    case "unavailable":
      return result;
    default:
      return result satisfies never;
  }
}

interface ConfirmEmailViewProps {
  state: ConfirmPageState;
  onRetry: () => void;
  onSignIn: () => void;
}

/** Разметка страницы подтверждения почты: ожидание, «Ссылка недействительна» или сообщение с повтором. */
export function ConfirmPageView({ state, onRetry, onSignIn }: ConfirmEmailViewProps) {
  switch (state) {
    case "confirming":
      return (
        <AuthLayout busy>
          <AuthStatus>Подтверждаем почту…</AuthStatus>
        </AuthLayout>
      );
    case "link-invalid":
      return (
        <AuthLayout>
          <div
            data-slot="invalid-confirmation"
            className="flex flex-col items-center gap-5 text-center"
          >
            <IconTile variant="destructive">
              <RiLinkUnlinkM className="size-[18px]" />
            </IconTile>
            <AuthHeading title="Ссылка недействительна">
              <p className="text-[13px] leading-normal text-muted-foreground">
                Войдите, чтобы получить новое письмо
              </p>
            </AuthHeading>
            <Button type="button" size="lg" className="w-full" onClick={onSignIn}>
              Войти
            </Button>
          </div>
        </AuthLayout>
      );
    case "unavailable":
      return (
        <AuthLayout>
          <div className="flex flex-col items-center gap-5 text-center">
            <AuthHeading title="Подтверждение почты" />
            <Alert variant="error" size="sm">
              <RiErrorWarningLine aria-hidden="true" />
              <span>Не удалось подтвердить почту. Попробуйте позже.</span>
            </Alert>
            <Button type="button" size="lg" variant="outline" className="w-full" onClick={onRetry}>
              Повторить
            </Button>
          </div>
        </AuthLayout>
      );
    default:
      return state satisfies never;
  }
}

interface ConfirmRunOptions {
  token: string;
  request: (token: string) => Promise<ConfirmEmailResult>;
  /** Читает обработчик в момент ответа: к нему `App` мог перерисоваться со свежим статусом сессии. */
  getOnConfirmed: () => () => void;
  setState: (state: ConfirmPageState) => void;
}

/** Один запрос подтверждения: успех зовёт актуальный `onConfirmed`, иное — состояние страницы. */
export function runConfirmation({
  token,
  request,
  getOnConfirmed,
  setState,
}: ConfirmRunOptions): Promise<void> {
  setState("confirming");
  return request(token).then((result) => {
    const next = confirmPageState(result);
    if (next === null) {
      getOnConfirmed()();
      return;
    }
    setState(next);
  });
}

interface ConfirmEmailPageProps {
  /** Токен из ссылки письма; страница сразу шлёт его демону. */
  token: string;
  /** Почта подтверждена (`204`, сессия не выдаётся): дальше вход или прежняя сессия. */
  onConfirmed: () => void;
  /** «Войти» на экране недействительной ссылки. */
  onSignIn: () => void;
  /** Отправитель — параметр, чтобы проверять без сети. */
  confirmRequest?: (token: string) => Promise<ConfirmEmailResult>;
}

/**
 * Страница подтверждения почты (`#/confirm/{token}`, stage9-email-confirmation,
 * Р7, Р11). Токен одноразовый: повторная отправка при перемонтировании в
 * `StrictMode` сожгла бы его, поэтому запрос идёт один раз на монтирование, а
 * «Повторить» — осознанный новый запрос.
 */
export function ConfirmEmailPage({
  token,
  onConfirmed,
  onSignIn,
  confirmRequest = confirmEmail,
}: ConfirmEmailPageProps) {
  const [state, setState] = useState<ConfirmPageState>("confirming");
  const started = useRef(false);
  // Запрос уходит один раз, а ответ приходит позже: обработчик из первого
  // рендера видел бы устаревший статус сессии, поэтому читаем его из ref.
  const onConfirmedRef = useRef(onConfirmed);
  onConfirmedRef.current = onConfirmed;

  const run = useCallback(() => {
    void runConfirmation({
      token,
      request: confirmRequest,
      getOnConfirmed: () => onConfirmedRef.current,
      setState,
    });
  }, [token, confirmRequest]);

  useEffect(() => {
    if (started.current) {
      return;
    }
    started.current = true;
    run();
  }, [run]);

  return <ConfirmPageView state={state} onRetry={run} onSignIn={onSignIn} />;
}
